import React, { useCallback, useMemo } from "react";

import { FunnelIcon } from "@heroicons/react/24/solid";

import SelectHookFormMulti from "../../../../components/SelectHookFormMulti";
import SearchButton from "../../../../components/SearchButton";
import RefreshButton from "../../../../components/RefreshButton";

import { useSFGetOffice } from "../../OfficeSettings/hooks/useSFGetOffice";

import type { OfficeRiskFilterProps, OfficeRiskSelectOption } from "../types";

const OfficeRiskFilter: React.FC<OfficeRiskFilterProps> = ({
  selectedOptions,
  onChange,
  onSearch,
  onRefresh,
  maxSelectedOffices = 20,
  isUpdateError = false,
}) => {
  const {
    data: agencies,
    isLoading: isOfficeOptionsLoading,
    isError: isOfficeOptionsError,
  } = useSFGetOffice();

  const officeOptions = useMemo<OfficeRiskSelectOption[]>(() => {
    const uniqueOptions = new Map<number, OfficeRiskSelectOption>();

    (agencies ?? []).forEach((office) => {
      const officeId = Number(office.officeId);

      if (!Number.isFinite(officeId) || officeId <= 0) {
        return;
      }

      const officeName = String(office.officeName ?? "").trim();

      const officeCode = String(office.officeCode ?? "").trim();

      const businessOperation = String(office.businessOperation ?? "").trim();

      const label =
        officeName && officeCode
          ? `${officeName} – ${officeCode}`
          : officeName || officeCode || `Office ${officeId}`;

      uniqueOptions.set(officeId, {
        label,
        value: officeId,
        name: businessOperation,
        id: officeCode,
      });
    });

    return Array.from(uniqueOptions.values()).sort((first, second) =>
      first.label.localeCompare(second.label)
    );
  }, [agencies]);

  const handleChange = useCallback(
    (selected: OfficeRiskSelectOption[] | null) => {
      const selectedValues = selected ?? [];

      onChange(selectedValues.slice(0, maxSelectedOffices));
    },
    [maxSelectedOffices, onChange]
  );

  return (
    <div className="w-full shrink-0 pb-1 pt-2 md:w-1/2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex shrink-0 items-center gap-1 text-xs font-semibold text-primary">
          <span>Filter By</span>

          <FunnelIcon className="h-4 w-4" />
        </div>

        <div className="min-w-0 flex-1">
          <SelectHookFormMulti
            inputId="blp-office-risk-filter"
            options={officeOptions}
            value={selectedOptions}
            onChange={handleChange}
            isLoading={isOfficeOptionsLoading}
            isMulti={true}
            placeholder={
              isOfficeOptionsError ? "Unable to load offices" : "Select offices"
            }
            secondDiv="items-center"
          />
        </div>

        <span className="shrink-0 whitespace-nowrap text-[10px] text-gray-500">
          {selectedOptions.length}/{maxSelectedOffices}
        </span>

        <div className="flex shrink-0 items-center gap-2">
          <SearchButton buttonValue="SEARCH" onClick={onSearch} />

          <RefreshButton buttonValue="" onClick={onRefresh} />
        </div>
      </div>

      {isUpdateError && (
        <div className="pt-1 text-left sm:pl-[136px]">
          <span className="text-[10px] text-red-600">
            Unable to update the chart.
          </span>
        </div>
      )}
    </div>
  );
};

export default OfficeRiskFilter;
